import * as XLSX from "xlsx-js-style";

export class ApplicationHelper {

  // Format money from backend, cents to dollars
  static formatMoney(cents, round = false, nullVal = "$0.00"){
    if(cents === null || cents === undefined) {
      return "—";
    }
    if(!cents) {
      return nullVal;
    }
    const amountInCents = parseInt(cents, 10);
    const dollars = amountInCents / 100.0;
    let formattedAmount = "";
    if(round) {
      formattedAmount = dollars.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    } else {
      formattedAmount = dollars.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    return `$${formattedAmount}`;
  }

  // Format number with commas
  static formatNumber(number) {
    if (number == null) return "0";
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /**
   * Date formatting function
   * @param dateString -> Any commonly formatted date string
   * @param dateOptions -> Object of date options for showing week day, time etc
   *  default -> {
   *    weekday: true, -> Shows the weekday in formatted string
   *    time: true, -> Shows time in formatted string
   *    year: true -> Shows year in formatted string
   *  }
   *    Pass any other dateOption values and it will get passed into the formatter
   *  | Option           | Possible Values                                                                         | Description                             |
   * | ---------------- | --------------------------------------------------------------------------------------- | --------------------------------------- |
   * | **weekday**      | `'narrow'`, `'short'`, `'long'`                                                         | Day of the week (e.g., "Mon", "Monday") |
   * | **era**          | `'narrow'`, `'short'`, `'long'`                                                         | AD/BC (or similar, depending on locale) |
   * | **year**         | `'numeric'`, `'2-digit'`                                                                | Full year or two-digit year             |
   * | **month**        | `'numeric'`, `'2-digit'`, `'narrow'`, `'short'`, `'long'`                               | Numeric or name of the month            |
   * | **day**          | `'numeric'`, `'2-digit'`                                                                | Day of the month                        |
   * | **hour**         | `'numeric'`, `'2-digit'`                                                                | Hour in 12/24 hour clock                |
   * | **minute**       | `'numeric'`, `'2-digit'`                                                                | Minute component                        |
   * | **second**       | `'numeric'`, `'2-digit'`                                                                | Second component                        |
   * | **timeZoneName** | `'short'`, `'long'`, `'shortOffset'`, `'longOffset'`, `'shortGeneric'`, `'longGeneric'` | Display time zone                       |
   * | **timeZone**     | IANA time zone name, e.g. `'UTC'`, `'America/New_York'`                                 | Sets specific time zone                 |
   * | **hour12**       | `true`, `false`                                                                         | 12-hour vs. 24-hour time                |
   * | **calendar**     | `'gregory'`, `'buddhist'`, `'japanese'`, etc.                                           | Calendar system to use                  |
   * | **dayPeriod**    | `'narrow'`, `'short'`, `'long'` (in some locales)                                       | Custom AM/PM representation             |
   * @returns {string} -> Formatted date String
   */
  static formatDate(dateString, dateOptions = {}) {
    if(!dateString) return "N/A";
    const date = new Date(dateString);

    dateOptions = {
      weekday: true,
      time: true,
      year: true,
      ...dateOptions
    };
    // Options for formatting the date
    const options = {
      month: 'long',      // e.g., 'August'
      day: 'numeric',     // e.g., '9'
    };

    if(dateOptions['weekday']) {
      options['weekday'] = 'long'; // e.g., 'Monday'
    }

    if(dateOptions['time']) {
      options['hour'] = "2-digit";
      options['minute'] = "2-digit";
    }
    if(dateOptions['year']) {
      options['year'] = 'numeric';
    }

    // Format the date using the options
    return date.toLocaleString('en-US', options);
  }

  static formatPaymentProcessorType(type){
    if(!type) return "Not completed";
    switch(type) {
      case "Processor::StripeProcessor":
        return "STRIPE";
        case "Processor::HelcimProcessor":
        return "HELCIM";
      case "Processor::OfflineProcessor":
        return "OFFLINE";
      default:
        return "Not Found";
    }
  }

  // Takes an excel file and returns json data as a promise
  // Only reads the first workbook, modify function if you want multiple
  static async excelFileToJson(file) {
    return new Promise((resolve, reject) => {
      if (file) {
        const reader = new FileReader();

        reader.onload = function(e) {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' }); // Read the workbook

            // Get the data from the first sheet
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];

            // Convert the data to JSON format
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            resolve(jsonData);
          } catch (error) {
            reject(error); // Reject the promise if there's an error
          }
        };

        reader.onerror = function(error) {
          reject(error); // Reject the promise if there's an error with FileReader
        };

        reader.readAsArrayBuffer(file); // Read the file
      } else {
        reject(new Error("No file provided"));
      }
    });
  }

  // Takes a file and returns a new one with cleaned name
  static sanitizeFileName(file) {
    // Removes invalid characters, replaces spaces with underscores
    const cleanedFileName = file.name.replace(/[\/\\:*?"<>|^'`\0]/g, '_').replace(/\s+/g, '_');
    return new File([file], cleanedFileName, {
      type: file.type,
    });
  }


  /**
   * Custom sort algorithm for campaigns / funds based json data structure getting saved
   * ie:
   *   {
   *             "uuid": "Fund-9bafe8b9-d444-4255-bc7a-5ae34e31ca9d",
   *             "order": 0
   *           },
   *           {
   *             "uuid": "Campaign-a6cdf08c-e0df-463f-a237-10e7cc26dfc3",
   *             "order": 1
   *           },
   *           {
   *             "uuid": "Campaign-3a60720f-f729-4ceb-9d5b-729a6937bc34",
   *             "order": 2
   *           },
   *  }
   * @param {Array} campaigns - Array of campaign objects from the API
   * @param {Array} customOrderMapping - Array of objects with uuid and order properties
   * @returns {Array} - Sorted array of campaigns
   */
  static sortCampaignsByCustomOrder(campaigns, customOrderMapping) {
    // Create a mapping of UUIDs to their custom order
    let orderMap = {};
    // This should be an array but gets returned as an object, handling both to be safe.
    if (typeof customOrderMapping === 'object') {
      Object.keys(customOrderMapping).forEach((index) => {
        let item = customOrderMapping[index];
        orderMap[item.uuid] = item.order;
      });
    } else if (Array.isArray(customOrderMapping)) {
      customOrderMapping.forEach(function (item) {
        orderMap[item.uuid] = item.order;
      });
    } else {
      // No sorting, return the default list
      return campaigns;
    }

    // Make a copy of the campaigns array to avoid mutating the original
    const sortedCampaigns = [...campaigns];
    // Sort campaigns based on custom order
    sortedCampaigns.sort((a, b) => {
      // Grab the order index from the created object
      if (orderMap[a.uuid] !== null && orderMap[b.uuid] !== null) {
        return orderMap[a.uuid] - orderMap[b.uuid];
      }

      // If only campaign A has a specified order, it comes first
      if (orderMap[a.uuid] !== null) {
        return -1;
      }

      // If only campaign B has a specified order, it comes first
      if (orderMap[b.uuid] !== null) {
        return 1;
      }

      // If neither has a specified order, maintain their original order
      return 0;
    });
    return sortedCampaigns;
  }

  static redirectToExternalUrl(websiteUrl, newWindow = false) {
    if(websiteUrl) {
      // Normalize the URL, links won't work without protocol
      if (!/^https?:\/\//i.test(websiteUrl)) {
        websiteUrl = 'https://' + websiteUrl;
      }
      // Ensure that the URL is valid
      const url = new URL(websiteUrl);

      if (url.protocol === 'http:' || url.protocol === 'https:') {
        if (newWindow) {
          window.open(url, '_blank');
        } else {
          window.location.href = url.href;
        }
      }
    }
  }


  // Generic function needed to parse different date formats coming from excel file
  // Excel dates can be a range of values, including integers that are not formatted the same as JS
  // Please forgive me
  static excelDateToJSDate(excelDate) {
    // Handle null, undefined, or empty values
    if (excelDate == null || excelDate === '') {
      return null;
    }

    // If it's already a Date object, return it
    if (excelDate instanceof Date) {
      return excelDate;
    }

    // If it's a string that looks like a standard date format
    if (typeof excelDate === 'string') {
      // Try parsing common date string formats first
      const parsedDate = new Date(excelDate);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate;
      }

      // If string parsing failed, try to convert to number for Excel serial date
      const numericValue = parseFloat(excelDate);
      if (isNaN(numericValue)) {
        return null;
      }
      excelDate = numericValue;
    }

    // Handle numeric Excel serial date
    if (typeof excelDate === 'number') {
      // Excel's epoch starts at January 1, 1900
      // But Excel incorrectly treats 1900 as a leap year, so we need to account for that
      // Excel serial date 1 = January 1, 1900
      // JavaScript Date epoch is January 1, 1970

      // Days between Excel epoch (1900-01-01) and JS epoch (1970-01-01)
      const excelEpoch = new Date(1900, 0, 1); // January 1, 1900
      const jsEpoch = new Date(1970, 0, 1);    // January 1, 1970
      const daysBetweenEpochs = (jsEpoch - excelEpoch) / (1000 * 60 * 60 * 24);

      // Adjust for Excel's leap year bug (Excel thinks 1900 was a leap year)
      let adjustedSerialDate = excelDate;
      if (excelDate >= 60) { // March 1, 1900 and later
        adjustedSerialDate = excelDate - 1;
      }

      // Convert to JavaScript timestamp
      const jsTimestamp = (adjustedSerialDate - daysBetweenEpochs) * 24 * 60 * 60 * 1000;

      return new Date(jsTimestamp);
    }

    return null;
  }

  static validateUrl(url) {
    if (!url) return true; // allow blank

    url = url.trim();
    try {
      // Add protocol if missing
      const urlToTest = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      const urlObj = new URL(urlToTest);

      // Must be http or https
      if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
        return false;
      }

      // Hostname must have at least one dot and a valid TLD (at least 2 chars after last dot)
      const parts = urlObj.hostname.split('.');
      return parts.length >= 2 && parts[parts.length - 1].length >= 2;
    } catch {
      return false;
    }
  }

}
